import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DataGrid,
  dataGridCellBorderLeftThickClassName,
  dataGridCellBorderRightThickClassName,
} from '@legacy/components/mui-components/DataGrid';
import { useGetLocale } from '@legacy/components/global/LocaleProvider';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_FIELD,
  GridActionsCellItem,
  GridCellCheckboxRenderer,
  GridColDef,
  GridPinnedRowsProp,
  GridRenderCellParams,
  GridRowModel,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router';
import isEqual from 'fast-deep-equal/es6';

import { legacyRoutes } from '@/constants/legacyRoutes';
import { useFilterStore } from '@legacy/stores/FilterStore';
import {
  IconButton,
  ToastifyAlert,
  Typography,
} from '@legacy/components/mui-components';
import { useTranslation } from 'react-i18next';
import {
  useDeleteProjects,
  useGetProjects,
  usePutProjects,
} from '@legacy/apis/projectsAPI';
import {
  Box,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  EditNote,
  Edit,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { formatTime } from '@legacy/utils/time';
import { Spinner, TextLink } from '@legacy/components/ui-components';
import { toast } from 'react-toastify';
import { useCellSingleClickMode } from '@legacy/components/mui-components/DataGrid/utils';
import { ViewOptionsChangeParameters } from '@legacy/components/layout/FilterLayout/types';
import { translationAnyText } from '@legacy/utils/translation';
import { stringToPascal } from '@legacy/utils/string';
import { TableNoData } from '@legacy/components/utils/TableNoData';
import { TViewOptions } from '@legacy/apis/savedViewAPI/types';
import { StyledTooltip } from '@legacy/components/global/StyledTooltip';
import { SearchProjectsSelectedFilterListStateKey } from '../../localStorageKeys';
import { Toolbar } from './Toolbar';

import styles from '../../SearchProjects.module.scss';
import { TimeRegistrationsDialog } from '../TimeRegistrationsDialog';
import { ProjectDeleteDialog } from '../ProjectDeleteDialog';
import { ProjectsErrorMessage } from '../../utils/serverResponseHandler';
import { statusValueOptions } from '../../constants';
import { IProjectError } from '../../types';
import { useHasNewNavigation } from '@/hooks/featureFlags';

interface ISearchProject {
  selectedViewOptions?: TViewOptions;
  onViewOptionsChange: (item: ViewOptionsChangeParameters[]) => void;
  filterSection?: ReactNode;
  rowHaveError: IProjectError[];
  setRowHaveError: Dispatch<SetStateAction<IProjectError[]>>;
}

const getColor = (value: number) => {
  if (value === 100) {
    return styles.progressInProgress;
  }
  if (value < 81) {
    return styles.progressPositive;
  }
  if (value < 100) {
    return styles.progressWarning;
  }
  return styles.progressNegative;
};

const hiddenFields = [
  GRID_CHECKBOX_SELECTION_FIELD,
  'hasAccountingIssues',
  'action',
];

const getTogglableColumns = (columns: GridColDef[]) =>
  columns
    .filter((column) => !hiddenFields.includes(column.field))
    .map((column) => column.field);

const LoadingOverlay = () => <Spinner />;

export const Table = ({
  selectedViewOptions,
  onViewOptionsChange,
  filterSection,
  rowHaveError,
  setRowHaveError,
}: ISearchProject) => {
  const { t } = useTranslation('searchProjects');
  const siteLocale = useGetLocale();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { filterQueryObj } = useFilterStore();

  localStorage.setItem(
    SearchProjectsSelectedFilterListStateKey,
    JSON.stringify(filterQueryObj),
  );

  const {
    isLoading,
    isSuccess,
    isEmpty,
    data: rows = [],
    summations,
    isShowingProjectStage,
    isShowingProjectCategory,
    refetch,
  } = useGetProjects(
    { selectedFilterList: filterQueryObj },
    selectedViewOptions,
  );

  const { mutate: updateProjects } = usePutProjects();
  const { mutateAsync: deleteProjects } = useDeleteProjects();

  const [selectedRowProject, setSelectedRowProject] = useState<string[]>([]);

  const handleRowSelectionChange = useMemo(
    () => () => {
      const selectedRows = apiRef.current?.getSelectedRows();
      if (selectedRows) {
        const formattedRows = Array.from(selectedRows.values()).map(
          (row) => row.projectId,
        );
        setSelectedRowProject(formattedRows);
      }
    },
    [apiRef, setSelectedRowProject],
  );

  useEffect(() => {
    refetch();
  }, [selectedViewOptions, filterQueryObj, refetch]);

  // Time Registration Dialog
  const [showTimeRegistration, setShowTimeRegistration] = useState(false);
  // Project delete dialog
  const [showProjectDelete, setShowProjectDelete] = useState(false);
  // Datagrid table click handler
  const { cellModesModel, handleCellClick, handleCellModesModelChange } =
    useCellSingleClickMode();

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [columnsButtonEl, setColumnsButtonEl] =
    useState<HTMLButtonElement | null>(null);

  const memoizedStatusValueOptions = useMemo(
    () =>
      statusValueOptions.map((option) => ({
        ...option,
        label: translationAnyText(t, option.label),
      })),
    [t],
  );

  const handleChange = useMemo(
    () => async (event: SelectChangeEvent, params: GridRenderCellParams) => {
      await apiRef.current?.setEditCellValue({
        id: params.id,
        field: params.field,
        value: event.target.value,
      });
      apiRef.current?.stopCellEditMode({ id: params.id, field: params.field });
    },
    [apiRef],
  );

  const renderAction = useCallback(
    (params: GridRowParams) => {
      const handleEditProjectClick = (projectId: string) => {
        navigate(legacyRoutes.projectManagement.planning(projectId));
      };

      const handleEditProjectSettingClick = (projectId: string) => {
        navigate(`/ProjectManagement/Setting/Index/${projectId}`);
      };

      if (params.id === 'TOTAL') {
        return [];
      }

      return [
        <GridActionsCellItem
          icon={
            <Tooltip title={t('EditProjectText')}>
              <Edit />
            </Tooltip>
          }
          label={t('EditProjectText')}
          onClick={(event) => {
            event.stopPropagation();
            handleEditProjectClick(params.row.projectId);
          }}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title={t('EditProjectSettingsText')}>
              <EditNote />
            </Tooltip>
          }
          label={t('EditProjectSettingsText')}
          onClick={(event) => {
            event.stopPropagation();
            handleEditProjectSettingClick(params.row.projectId);
          }}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title={t('DeleteProjectText')}>
              <Delete />
            </Tooltip>
          }
          label={t('DeleteProjectText')}
          onClick={() => {
            setSelectedProjectId(params.row.projectId);
            setSelectedRowProject([]);
            setShowProjectDelete(true);
          }}
        />,
      ];
    },
    [navigate, t],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        cellClassName: (params) => {
          const hasError =
            Array.isArray(rowHaveError) &&
            rowHaveError?.some(
              (row) =>
                row.projectId === params.row.projectId &&
                row.isDeletedSuccessfully === false,
            );
          return hasError ? 'error-selected-cell' : '';
        },
        renderCell: (params) => {
          const error =
            Array.isArray(rowHaveError) &&
            rowHaveError?.find(
              (row) =>
                row.projectId === params.row.projectId &&
                row.isDeletedSuccessfully === false,
            );

          if (!error) {
            return <GridCellCheckboxRenderer {...params} />;
          }

          return (
            <StyledTooltip
              title={
                <div>
                  {error.details.map(({ errorCode, message }) => (
                    <Box sx={{ mb: 1 }} key={message}>
                      {translationAnyText(
                        t,
                        `errors.${ProjectsErrorMessage[errorCode]}`,
                      )}
                    </Box>
                  ))}
                </div>
              }
              status="error"
              placement="right"
              followCursor={false}
            >
              <GridCellCheckboxRenderer {...params} />
            </StyledTooltip>
          );
        },
      },
      {
        field: 'hasAccountingIssues',
        headerName: '',
        hideable: false,
        width: 40,
        display: 'flex',
        disableExport: true,
        renderCell: (params: GridRenderCellParams) =>
          params.value ? (
            <IconButton
              data-automation-id="AccountingIssuesIcon"
              title={t('PendingBookingInfo')}
              size="small"
              onClick={() =>
                window.open(
                  `/ProjectManagement/Booking/List/${params.row?.projectId}`,
                  '_blank',
                )
              }
            >
              <WarningAmberOutlined fontSize="small" />
            </IconButton>
          ) : null,
      },
      {
        field: 'no',
        headerName: t('TableHeaderProjectNo'),
        minWidth: 100,
        flex: 0.1,
        renderCell: (params: GridRenderCellParams) => (
          <TextLink
            key={0}
            className={styles.link}
            useTextColor
            href={legacyRoutes.projectManagement.planning(params.row.projectId)}
            title={params.value}
          >
            {params.value}
          </TextLink>
        ),
        valueGetter: (value: any, row: { id: string; name: string }) => {
          if (row.id === 'TOTAL') {
            return row.name;
          }
          return value;
        },
        colSpan: (_value: any, row: { id: string }) => {
          if (row.id === 'TOTAL') {
            return 2;
          }
          return undefined;
        },
      },
      {
        field: 'name',
        headerName: t('TableHeaderProjectName'),
        minWidth: 250,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TextLink
            key={0}
            className={styles.link}
            useTextColor
            href={legacyRoutes.projectManagement.planning(params.row.projectId)}
            title={params.value}
          >
            {params.value}
          </TextLink>
        ),
      },
      {
        field: 'completion',
        headerName: t('TableHeaderCompletionMeter'),
        minWidth: 120,
        renderCell: (params: GridRenderCellParams) => {
          if (params.id === 'TOTAL') {
            return '';
          }
          const value = parseInt(params.value as string, 10);
          const labelId = `linear-progress-label-${params.id}`;
          return (
            value >= 0 && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LinearProgress
                  className={getColor(value)}
                  variant="determinate"
                  value={Math.min(value, 100)}
                  aria-labelledby={labelId}
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '58%',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                />
                <Typography
                  id={labelId}
                  sx={{ position: 'absolute', color: 'white' }}
                >
                  {`${value}%`}
                </Typography>
              </Box>
            )
          );
        },
      },
      {
        field: 'customerName',
        headerName: t('TableHeaderCustomer'),
        minWidth: 120,
        flex: 0.1,
        renderCell: (params: GridRenderCellParams) => (
          <TextLink
            key={0}
            className={styles.link}
            useTextColor
            href={`/CRM/Customer/Edit/${params.row.customerId}`}
            title={params.value}
          >
            {params.value}
          </TextLink>
        ),
      },
      {
        field: 'projectManagerInitials',
        headerName: t('TableHeaderProjectManager'),
        minWidth: 120,
        flex: 0.1,
        renderCell: (params) => (
          <span title={params.row.projectManagerName}>{params.value}</span>
        ),
      },
      {
        field: 'accountManagerInitials',
        headerName: t('TableHeaderAccountManager'),
        minWidth: 120,
        flex: 0.1,
        renderCell: (params) => (
          <span title={params.row.accountManagerName}>{params.value}</span>
        ),
      },
      {
        field: 'projectTypeName',
        headerName: t('TableHeaderProjectType'),
        minWidth: 130,
        flex: 0.1,
      },
      ...(isShowingProjectCategory
        ? [
            {
              field: 'projectCategoryName',
              headerName: t('TableHeaderProjectCategory'),
              minWidth: 130,
              flex: 0.1,
            },
          ]
        : []),
      ...(isShowingProjectStage
        ? [
            {
              field: 'projectStageName',
              headerName: t('TableHeaderProjectStage'),
              minWidth: 130,
              flex: 0.1,
            },
          ]
        : []),
      {
        field: 'budgetWorkHours',
        headerName: t('TableHeaderProjectBudgetHours'),
        minWidth: 120,
        flex: 0.1,
        headerAlign: 'right',
        align: 'right',
        headerClassName: `${dataGridCellBorderLeftThickClassName}`,
        cellClassName: `${dataGridCellBorderLeftThickClassName}`,
        valueFormatter: (value) => formatTime(value, siteLocale),
      },
      {
        field: 'tasksBudgetHours',
        headerName: t('TableHeaderTaskBudgetHours'),
        minWidth: 120,
        flex: 0.1,
        headerAlign: 'right',
        align: 'right',
        valueFormatter: (value) => formatTime(value, siteLocale),
      },
      {
        field: 'allocationsBudgetHours',
        headerName: t('TableHeaderAllocatedBudgetHours'),
        minWidth: 120,
        flex: 0.1,
        headerAlign: 'right',
        align: 'right',
        headerClassName: `${dataGridCellBorderRightThickClassName}`,
        cellClassName: `${dataGridCellBorderRightThickClassName}`,
        valueFormatter: (value) => formatTime(value, siteLocale),
      },
      {
        field: 'projectActualHours',
        headerName: t('TableHeaderActualHours'),
        minWidth: 120,
        flex: 0.1,
        headerAlign: 'right',
        align: 'right',
        display: 'flex',
        valueFormatter: (value) => formatTime(value, siteLocale),
        renderCell: (params: GridRenderCellParams) => (
          <Box position="relative">
            {params.id !== 'TOTAL' && params.value !== 0 && (
              <IconButton
                data-automation-id="TimeRegistrationIcon"
                className={styles.cornerInfo}
                title={t('ViewTimeRegistrationText')}
                onClick={() => {
                  setSelectedProjectId(params.row.projectId);
                  setShowTimeRegistration(true);
                }}
              />
            )}
            {formatTime(params.value, siteLocale)}
          </Box>
        ),
      },
      {
        field: 'action',
        headerName: '',
        type: 'actions',
        minWidth: 130,
        flex: 0.1,
        getActions: renderAction,
      },
      {
        field: 'statusId',
        headerName: t('TableHeaderStatus'),
        minWidth: 130,
        flex: 0.1,
        editable: true,
        display: 'flex',
        renderEditCell: (params: GridRenderCellParams) => (
          <Select
            open
            value={params.value}
            onChange={(event) => handleChange(event, params)}
            onClose={() => {
              apiRef.current?.stopCellEditMode({
                id: params.id,
                field: params.field,
              });
            }}
            sx={{ width: '100%' }}
          >
            {memoizedStatusValueOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        ),
        renderCell: (params: GridRenderCellParams) => {
          const statusOption = memoizedStatusValueOptions.find(
            (option) => option.value === params.row.statusId,
          );
          const Icon = statusOption ? statusOption.icon : null;
          return (
            <Box display="flex" alignItems="center">
              {Icon ? <Icon fontSize="small" /> : null}
              <Typography sx={{ ml: 1, fontSize: 12 }}>
                {statusOption?.label}
              </Typography>
            </Box>
          );
        },
      },
    ],
    [
      t,
      isShowingProjectStage,
      isShowingProjectCategory,
      renderAction,
      rowHaveError,
      siteLocale,
      memoizedStatusValueOptions,
      handleChange,
      apiRef,
    ],
  );

  const pinnedRows: GridPinnedRowsProp = {
    bottom: [...[summations]],
  };

  const applyProjectsUpdate = async (
    uri?: string,
    validDate?: Date,
    action?: any,
  ) => {
    if (uri === 'delete') {
      await deleteProjects(selectedRowProject, {
        onSuccess: () => {
          setRowHaveError([]);
          toast.success(
            <ToastifyAlert description={t('ToastProjectsDeletedText')} />,
            {
              autoClose: 5000,
              closeButton: false,
            },
          );
        },
        onError: (error) => {
          setRowHaveError(error as unknown as IProjectError[]);
          toast.error(
            <ToastifyAlert description={t('errors.UnknownErrorMessage')} />,
            {
              autoClose: 5000,
              closeButton: false,
            },
          );
        },
      });
    } else if (uri === 'status') {
      await updateProjects(
        {
          updateModel: {
            status: action?.status,
            date: validDate,
          },
          fieldName: uri,
          projectsId: selectedRowProject,
        },
        {
          onSuccess: (responseData) => {
            toast.success(
              <ToastifyAlert
                description={`${responseData?.properties?.updatedProjectsCount} ${t(
                  'ToastProjectsStatusUpdatedText',
                )} ${translationAnyText(t, `Status${stringToPascal(action?.value)}Text`)}`}
              />,
              {
                autoClose: 5000,
                closeButton: false,
              },
            );
          },
          onError: () => {
            toast.error(
              <ToastifyAlert description={t('errors.UnknownErrorMessage')} />,
              {
                autoClose: 5000,
                closeButton: false,
              },
            );
          },
        },
      );
    } else if (uri === 'stage') {
      await updateProjects(
        {
          updateModel: {
            stage: action?.stage,
            date: validDate,
          },
          fieldName: uri,
          projectsId: selectedRowProject,
        },
        {
          onSuccess: (responseData) => {
            toast.success(
              <ToastifyAlert
                description={`${responseData?.properties?.updatedProjectsCount} ${t(
                  'ToastProjectsStageUpdatedText',
                )} ${action?.value}`}
              />,
              {
                autoClose: 5000,
                closeButton: false,
              },
            );
          },
          onError: () => {
            toast.error(
              <ToastifyAlert description={t('errors.UnknownErrorMessage')} />,
              {
                autoClose: 5000,
                closeButton: false,
              },
            );
          },
        },
      );
    }
  };

  // Handle single project status update on datagrid
  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      if (newRow.statusId === oldRow.statusId) {
        return newRow;
      }
      await updateProjects(
        {
          updateModel: {
            status: newRow.statusId,
          },
          fieldName: 'status',
          projectsId: [newRow.projectId],
        },
        {
          onSuccess: () => {
            const statusLabel =
              statusValueOptions.find(
                (option) => option.value === newRow.statusId,
              )?.label || 'DefaultStatusText';

            toast.success(
              <ToastifyAlert
                description={`${t('ToastProjectStatusUpdatedText')} ${translationAnyText(t, statusLabel)} `}
              />,
              {
                autoClose: 5000,
                closeButton: false,
              },
            );
          },
          onError: () => {
            toast.error(
              <ToastifyAlert description={t('errors.UnknownErrorMessage')} />,
              {
                autoClose: 5000,
                closeButton: false,
              },
            );
          },
        },
      );

      return newRow;
    },
    [t, updateProjects],
  );

  const [newData, setNewData] = useState(rows);

  useEffect(() => {
    if (!isLoading && !isEqual(newData, rows)) {
      setNewData(rows);
    }
  }, [isLoading, isSuccess, rows, newData]);

  // Height of the table
  const dataGridContainerRef = useRef<HTMLDivElement>(null);
  const [offSetTop, setOffSetTop] = useState<number>(0);

  const hasNewNavigation = useHasNewNavigation();

  // Set on initial load
  useEffect(() => {
    if (dataGridContainerRef.current) {
      setOffSetTop(dataGridContainerRef.current.getBoundingClientRect().top);
    }
  }, [isSuccess, hasNewNavigation]);

  // Reduce max height by offset plus the height of bottom padding and wrapping elements of current layout
  // 44px for new navigation and 80px for legacy navigation
  const tableMaxHeight = hasNewNavigation
    ? `calc(100vh - ${offSetTop + 44}px)`
    : `calc(100vh - ${offSetTop + 80}px)`;

  return (
    <div
      ref={dataGridContainerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: isEmpty ? '350px' : 0,
        maxHeight: tableMaxHeight,
      }}
    >
      <DataGrid
        apiRef={apiRef}
        columns={columns}
        rows={newData}
        disableColumnMenu
        hideFooter
        checkboxSelection
        disableRowSelectionOnClick
        pinnedRows={!isEmpty ? pinnedRows : undefined}
        onRowSelectionModelChange={() => handleRowSelectionChange()}
        rowHeight={34}
        loading={isLoading}
        initialState={{
          columns: {
            columnVisibilityModel: {
              accountManagerInitials: false,
              tasksBudgetHours: false,
              allocationsBudgetHours: false,
            },
          },
        }}
        processRowUpdate={processRowUpdate}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        onCellClick={handleCellClick}
        isCellEditable={(params) => params.id !== 'TOTAL'}
        slots={{
          // Custom toolbar component with different props than MUI's expected GridToolbarProps
          toolbar: Toolbar as any,
          loadingOverlay: LoadingOverlay,
          // eslint-disable-next-line react/no-unstable-nested-components
          noRowsOverlay: () => (
            <TableNoData onViewOptionsChange={onViewOptionsChange} />
          ),
        }}
        // To hide the toolbar text
        localeText={{
          toolbarColumns: '',
          toolbarFilters: '',
          toolbarDensity: '',
          toolbarExport: '',
        }}
        slotProps={{
          ...(columnsButtonEl && {
            panel: {
              anchorEl: columnsButtonEl,
              placement: 'bottom-end',
            } as any,
          }),
          // Custom props for our toolbar component (not part of standard MUI GridToolbarProps)
          toolbar: {
            selectedRowProject,
            columns,
            setColumnsButtonEl,
            filterSection,
            applyProjectsUpdate,
          } as any,
          columnsManagement: {
            getTogglableColumns,
          },
          root: {
            'data-automation-id': 'SearchProjectsTable',
          },
        }}
        showToolbar
        sx={{
          fontSize: 12,
        }}
      />
      {/* Time registration Dialog */}
      {showTimeRegistration && selectedProjectId && (
        <TimeRegistrationsDialog
          showTimeRegistration={showTimeRegistration}
          setShowTimeRegistration={setShowTimeRegistration}
          projectId={selectedProjectId}
        />
      )}
      {/* Project delete Dialog */}
      <ProjectDeleteDialog
        showProjectDelete={showProjectDelete}
        setShowProjectDelete={setShowProjectDelete}
        projectId={selectedProjectId}
        selectedProjectsId={selectedRowProject}
        applyProjectsUpdate={applyProjectsUpdate}
      />
    </div>
  );
};
